<template>
  <v-dialog
    :value="value"
    :persistent="isStreaming || isSaving"
    max-width="800"
    content-class="rename-responsibility-dialog__content-class"
    @input="onDialogInput"
  >
    <v-card class="rename-responsibility-dialog" width="800">
      <!-- Modal header -->
      <div class="rename-responsibility-dialog__header">
        <div class="rename-responsibility-dialog__featured-icon" aria-hidden="true">
          <ResponsibilityIcon color="dark" size="normal" />
        </div>
        <div class="rename-responsibility-dialog__heading">
          <h2 class="rename-responsibility-dialog__title">Rename Responsibility</h2>
        </div>
        <button
          type="button"
          class="rename-responsibility-dialog__close"
          aria-label="Close dialog"
          @click="onClose"
        >
          <v-icon>mdi-close</v-icon>
        </button>
      </div>

      <!-- Body -->
      <RoleNameContextBody
        :name.sync="name"
        :context.sync="context"
        :error-message="saveError || nameValidation.error.value"
        :excluded-name="responsibility.name"
        :is-archived="isArchived"
        :show-replace-popover.sync="showReplacePopover"
        :max-name-characters="maxNameCharacters"
        variant="responsibility"
        name-id="rename-responsibility-dialog__name"
        context-id="rename-responsibility-dialog__context"
        @validity-change="onValidityChange"
        @streaming-change="onStreamingChange"
        @clear-error="clearSaveError"
      />

      <!-- Footer -->
      <div class="rename-responsibility-dialog__footer">
        <button
          type="button"
          class="rename-responsibility-dialog__save"
          :disabled="!canSave"
          @click="handleSave"
        >
          Save
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "RenameResponsibilityDialog",
};
</script>

<script setup>
import { ref, computed, watch } from "vue";
import RoleNameContextBody from "@components/RoleNameContextBody/RoleNameContextBody.vue";
import ResponsibilityIcon from "@components/icons/items/OrgObject/ResponsibilityIcon.vue";
import {
  updateNameResponsibility,
  updateResponsibilityContext,
  getResponsibilityIdV2,
} from "@/api";
import { useOrgObjectNameValidation } from "@/composables/useOrgObjectNameValidation";

const CONTEXT_MAX = 500;

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  responsibility: {
    type: Object,
    required: true,
  },
  maxNameCharacters: {
    type: Number,
    default: 120,
  },
});

const emit = defineEmits(["input", "saved"]);

const name = ref("");
const context = ref("");
const originalContext = ref("");
const loadedArchived = ref(false);
const isContextLoading = ref(false);
const isStreaming = ref(false);
const isSaving = ref(false);
const isNameValid = ref(false);
const showReplacePopover = ref(false);
const saveError = ref("");

const originalName = computed(() => (props.responsibility?.name || "").trim());

const nameValidation = useOrgObjectNameValidation({
  type: "responsibility",
  excludeId: () => props.responsibility?.id,
});

// The responsibility's own (unchanged) name is never sent to the availability check.
watch(name, (value) => {
  nameValidation.name.value = value.trim() === originalName.value ? "" : value;
});

const nameChanged = computed(() => name.value.trim() !== originalName.value);
const contextChanged = computed(() => context.value.trim() !== originalContext.value.trim());
const contextTooLong = computed(() => context.value.length > CONTEXT_MAX);

const isArchived = computed(() => {
  const responsibility = props.responsibility || {};
  return (
    responsibility.archived === true ||
    responsibility.status === "archived" ||
    loadedArchived.value
  );
});

const canSave = computed(
  () =>
    (nameChanged.value || contextChanged.value) &&
    isNameValid.value &&
    !contextTooLong.value &&
    !nameValidation.checking.value &&
    !isContextLoading.value &&
    !isStreaming.value &&
    !isSaving.value
);

function resolveAccountId() {
  try {
    const raw = localStorage.getItem("currentAccountID");
    if (raw == null || raw === "") return null;
    const parsed = JSON.parse(raw);
    return parsed != null ? parsed : raw;
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function onValidityChange(value) {
  isNameValid.value = value;
}

function onStreamingChange(value) {
  isStreaming.value = value;
}

function clearSaveError() {
  if (saveError.value) saveError.value = "";
}

function onDialogInput(nextValue) {
  if (nextValue === true) {
    emit("input", true);
    return;
  }

  if (showReplacePopover.value) {
    showReplacePopover.value = false;
    return;
  }

  if (isStreaming.value || isSaving.value) return;

  saveError.value = "";
  emit("input", false);
}

function onClose() {
  if (isStreaming.value || isSaving.value) return;
  if (showReplacePopover.value) {
    showReplacePopover.value = false;
    return;
  }
  saveError.value = "";
  emit("input", false);
}

async function handleSave() {
  if (!canSave.value) return;

  const responsibilityId = props.responsibility?.id;
  if (!responsibilityId) {
    saveError.value = "Unable to save: responsibility ID is missing.";
    return;
  }

  const accountId = resolveAccountId();
  if (accountId == null) {
    saveError.value = "Select an account to save.";
    return;
  }

  isSaving.value = true;
  saveError.value = "";

  try {
    const updatedResponsibility = { ...(props.responsibility || {}) };

    if (nameChanged.value) {
      const renamed = await updateNameResponsibility(accountId, responsibilityId, {
        name: name.value.trim(),
        account: accountId,
      });
      updatedResponsibility.name = name.value.trim();
      if (renamed?.data?.code_str) updatedResponsibility.code_str = renamed.data.code_str;
    }

    if (contextChanged.value) {
      await updateResponsibilityContext(accountId, responsibilityId, {
        context: context.value.trim(),
      });
      updatedResponsibility.context = context.value.trim();
    }

    emit("saved", updatedResponsibility);
    emit("input", false);
  } catch (error) {
    const apiError = error?.response?.data?.[0];
    if (nameValidation.validateOnSubmitError(error)) {
      // Inline "name in use" is rendered from nameValidation.error.
    } else if (typeof apiError === "string" && apiError.trim()) {
      saveError.value = apiError;
    } else {
      saveError.value = "An error occurred while saving the responsibility.";
    }
  } finally {
    isSaving.value = false;
  }
}

// Rows that do not carry the Context (or the archived flag) are read from the
// Responsibility itself, so the dialog never opens with an empty field for a
// Responsibility that has Context.
async function loadMissingDetails() {
  const responsibilityId = props.responsibility?.id;
  const accountId = resolveAccountId();
  if (!responsibilityId || accountId == null) return;

  isContextLoading.value = true;
  try {
    const { data } = await getResponsibilityIdV2(accountId, responsibilityId, "responsibility");
    const loadedContext = data?.context || "";
    if (props.value && context.value === originalContext.value) {
      context.value = loadedContext;
    }
    originalContext.value = loadedContext;
    loadedArchived.value = data?.archived === true;
  } catch (error) {
    saveError.value = "Couldn't load the current Context. Try again.";
  } finally {
    isContextLoading.value = false;
  }
}

function resetFromResponsibility() {
  name.value = props.responsibility?.name || "";
  context.value = props.responsibility?.context || "";
  originalContext.value = context.value;
  loadedArchived.value = false;
}

watch(
  () => props.value,
  (open) => {
    if (!open) return;

    resetFromResponsibility();
    saveError.value = "";
    nameValidation.error.value = "";
    showReplacePopover.value = false;
    isSaving.value = false;
    if (props.responsibility?.context === undefined) loadMissingDetails();
  },
  { immediate: true }
);

watch(
  () => props.responsibility,
  () => {
    if (props.value) resetFromResponsibility();
  }
);
</script>

<style scoped>
.rename-responsibility-dialog {
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.rename-responsibility-dialog__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 24px 0 24px;
}

.rename-responsibility-dialog__featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #eaecf0;
}

.rename-responsibility-dialog__heading {
  flex: 1;
  min-width: 0;
}

.rename-responsibility-dialog__title {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #101828;
}

.rename-responsibility-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  cursor: pointer;
}

.rename-responsibility-dialog__close:hover,
.rename-responsibility-dialog__close:focus-visible {
  background: #f9fafb;
  outline: none;
}

.rename-responsibility-dialog__footer {
  padding: 0 24px 24px 24px;
}

.rename-responsibility-dialog__save {
  width: 100%;
  height: 44px;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  background: #7f56d9;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;
}

.rename-responsibility-dialog__save:hover:not(:disabled),
.rename-responsibility-dialog__save:focus-visible {
  background: #6941c6;
  border-color: #6941c6;
  outline: none;
}

.rename-responsibility-dialog__save:disabled {
  background: #e0e0e0;
  border-color: #e0e0e0;
  color: #475467;
  cursor: not-allowed;
}
</style>
